<template>
  <div class="page">
    <div class="container">
      <div class="page-header">
        <h1>Manage Legislative Calendar</h1>
        <Button label="+ Add Schedule" icon="pi pi-plus" @click="openAddModal" />
      </div>

      <DataTable
        :value="filteredCalendar"
        :paginator="true"
        :rows="10"
        :loading="loading"
        stripedRows
        showGridlines
        v-model:filters="filters"
        filterDisplay="menu"
      >
        <template #header>
          <div class="table-header">
            <IconField iconPosition="left">
              <InputIcon class="pi pi-search" />
              <InputText v-model="filters['global'].value" placeholder="Search..." />
            </IconField>
            <div class="header-filters">
              <Select v-model="selectedCongress" :options="congressOptions" optionLabel="label" optionValue="value" placeholder="Congress" @change="loadCalendar" class="filter-select" />
              <Select v-model="selectedSession" :options="sessionFilterOptions" optionLabel="label" optionValue="value" placeholder="All Sessions" showClear class="filter-select" />
            </div>
          </div>
        </template>

        <Column field="congress_no" header="Congress" sortable>
          <template #body="{ data }">
            <span class="congress-badge">{{ data.congress_no }}th</span>
          </template>
        </Column>
        <Column field="session" header="Session" sortable>
          <template #body="{ data }">
            <span class="session-badge">{{ data.session }}</span>
          </template>
        </Column>
        <Column field="schedule" header="Schedule" sortable>
          <template #body="{ data }">
            <span class="schedule-badge" :class="getScheduleClass(data.schedule)">{{ data.schedule }}</span>
          </template>
        </Column>
        <Column field="date_from" header="Date From" sortable>
          <template #body="{ data }">{{ formatDate(data.date_from) }}</template>
        </Column>
        <Column field="date_to" header="Date To" sortable>
          <template #body="{ data }">{{ formatDate(data.date_to) }}</template>
        </Column>
        <Column header="Actions" style="width: 10rem">
          <template #body="{ data }">
            <div class="actions">
              <Button icon="pi pi-pencil" severity="info" size="small" @click="openEditModal(data)" v-tooltip="'Edit'" />
              <Button icon="pi pi-trash" severity="danger" size="small" @click="deleteEntry(data.id)" v-tooltip="'Delete'" />
            </div>
          </template>
        </Column>
      </DataTable>
    </div>

    <!-- Add/Edit Schedule Dialog -->
    <Dialog v-model:visible="showModal" :header="isEditing ? 'Edit Schedule' : 'Add Schedule'" :modal="true" :style="{width: '500px'}">
      <form @submit.prevent="saveEntry" class="modal-form">
        <div class="form-grid">
          <div class="form-group">
            <FloatLabel variant="in">
              <Select id="form_congress" v-model="form.congress_no" :options="congressOptions" optionLabel="label" optionValue="value" class="w-full" />
              <label for="form_congress">Congress *</label>
            </FloatLabel>
          </div>
          <div class="form-group">
            <FloatLabel variant="in">
              <Select id="form_session" v-model="form.session" :options="sessionOptions" optionLabel="label" optionValue="value" class="w-full" />
              <label for="form_session">Session *</label>
            </FloatLabel>
          </div>
          <div class="form-group">
            <FloatLabel variant="in">
              <Select id="form_schedule" v-model="form.schedule" :options="scheduleOptions" optionLabel="label" optionValue="value" class="w-full" />
              <label for="form_schedule">Schedule *</label>
            </FloatLabel>
          </div>
          <div class="form-row-2">
            <div class="form-group">
              <FloatLabel variant="in">
                <DatePicker id="form_date_from" v-model="form.date_from" dateFormat="yy-mm-dd" class="w-full" />
                <label for="form_date_from">Date From *</label>
              </FloatLabel>
            </div>
            <div class="form-group">
              <FloatLabel variant="in">
                <DatePicker id="form_date_to" v-model="form.date_to" dateFormat="yy-mm-dd" class="w-full" />
                <label for="form_date_to">Date To *</label>
              </FloatLabel>
            </div>
          </div>
        </div>
        <div class="modal-actions">
          <Button type="button" label="Cancel" severity="secondary" icon="pi pi-times" @click="closeModal" />
          <Button type="submit" :label="isEditing ? 'Update' : 'Save'" icon="pi pi-check" :loading="saving" />
        </div>
      </form>
    </Dialog>
  </div>
</template>

<script>
import Swal from 'sweetalert2'
import api from '../axios.js'

export default {
  data() {
    return {
      selectedCongress: '20',
      selectedSession: null,
      calendar: [],
      loading: false,
      saving: false,
      showModal: false,
      isEditing: false,
      filters: {
        global: { value: null, matchMode: 'contains' }
      },
      form: {
        id: null,
        congress_no: '20',
        session: '',
        schedule: '',
        date_from: null,
        date_to: null
      },
      congressOptions: [
        { label: '20th Congress', value: '20' },
        { label: '19th Congress', value: '19' }
      ],
      sessionFilterOptions: [
        { label: 'First Session', value: 'First' },
        { label: 'Second Session', value: 'Second' },
        { label: 'Third Session', value: 'Third' }
      ],
      sessionOptions: [
        { label: 'First', value: 'First' },
        { label: 'Second', value: 'Second' },
        { label: 'Third', value: 'Third' }
      ],
      scheduleOptions: [
        { label: 'Commencement of Session', value: 'Commencement of Session' },
        { label: 'Adjournment of Session', value: 'Adjournment of Session' },
        { label: 'Resumption of Session', value: 'Resumption of Session' },
        { label: 'Suspension of Session', value: 'Suspension of Session' }
      ]
    }
  },
  computed: {
    filteredCalendar() {
      return this.calendar.filter(entry => {
        return !this.selectedSession || entry.session === this.selectedSession
      })
    }
  },
  methods: {
    async loadCalendar() {
      this.loading = true
      try {
        const response = await api.get(`/calendar?congress_no=${this.selectedCongress}`)
        this.calendar = response.data
      } catch (error) {
        console.error('Error loading calendar:', error)
        this.calendar = []
      } finally {
        this.loading = false
      }
    },
    openAddModal() {
      this.isEditing = false
      this.form = {
        id: null,
        congress_no: this.selectedCongress,
        session: '',
        schedule: '',
        date_from: null,
        date_to: null
      }
      this.showModal = true
    },
    openEditModal(entry) {
      this.isEditing = true
      this.form = {
        ...entry,
        date_from: entry.date_from ? new Date(entry.date_from) : null,
        date_to: entry.date_to ? new Date(entry.date_to) : null
      }
      this.showModal = true
    },
    closeModal() {
      this.showModal = false
      this.saving = false
    },
    async saveEntry() {
      if (!this.form.congress_no || !this.form.session || !this.form.schedule || !this.form.date_from || !this.form.date_to) {
        Swal.fire({ title: 'Validation', text: 'Please fill in all fields.', icon: 'warning' })
        return
      }

      const result = await Swal.fire({
        title: 'Confirm',
        text: `Are you sure you want to ${this.isEditing ? 'update' : 'add'} this schedule?`,
        icon: 'question',
        showCancelButton: true,
        confirmButtonText: this.isEditing ? 'Update' : 'Save',
        cancelButtonText: 'Cancel'
      })
      if (!result.isConfirmed) return

      this.saving = true
      try {
        const data = {
          congress_no: this.form.congress_no,
          session: this.form.session,
          schedule: this.form.schedule,
          date_from: this.form.date_from,
          date_to: this.form.date_to
        }

        if (this.isEditing) {
          await api.put(`/calendar/${this.form.id}`, data)
        } else {
          await api.post('/calendar', data)
        }

        this.closeModal()
        this.loadCalendar()
        Swal.fire({ title: 'Success!', text: `Schedule ${this.isEditing ? 'updated' : 'added'} successfully.`, icon: 'success', timer: 1500, showConfirmButton: false })
      } catch (error) {
        console.error('Error saving entry:', error)
        Swal.fire({ title: 'Error!', text: 'Failed to save schedule.', icon: 'error' })
      } finally {
        this.saving = false
      }
    },
    async deleteEntry(id) {
      const result = await Swal.fire({
        title: 'Delete Schedule',
        text: 'Are you sure you want to delete this schedule?',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Delete',
        cancelButtonText: 'Cancel',
        confirmButtonColor: '#ef4444'
      })

      if (result.isConfirmed) {
        try {
          await api.delete(`/calendar/${id}`)
          this.loadCalendar()
          Swal.fire({ title: 'Deleted!', text: 'Schedule deleted successfully.', icon: 'success', timer: 1500, showConfirmButton: false })
        } catch (error) {
          console.error('Error deleting entry:', error)
          Swal.fire({ title: 'Error!', text: 'Failed to delete schedule.', icon: 'error' })
        }
      }
    },
    formatDate(dateString) {
      if (!dateString) return '—'
      return new Date(dateString).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })
    },
    getScheduleClass(schedule) {
      const classes = {
        'Commencement of Session': 'commencement',
        'Adjournment of Session': 'adjournment',
        'Resumption of Session': 'resumption',
        'Suspension of Session': 'suspension'
      }
      return classes[schedule] || 'default'
    },
    async loadCurrentCongress() {
      try {
        const response = await api.get('/current-congress')
        this.selectedCongress = response.data.congress_no || '20'
      } catch (error) {
        this.selectedCongress = '20'
      }
    }
  },
  async mounted() {
    await this.loadCurrentCongress()
    this.loadCalendar()
  }
}
</script>

<style scoped>
.page {
  padding: 1rem 0;
  background: var(--p-surface-50);
  min-height: 100%;
}

.container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 2rem;
}

.page-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;
  padding-bottom: 1rem;
  border-bottom: 2px solid #1e3a8a;
}

.page-header h1 {
  color: #1e3a8a;
  font-size: 1.75rem;
  font-weight: 700;
}

.table-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 1rem;
  flex-wrap: wrap;
}

.header-filters {
  display: flex;
  gap: 0.75rem;
  align-items: center;
}

.filter-select {
  min-width: 180px;
}

.congress-badge {
  background: #1e3a8a;
  color: white;
  padding: 0.2rem 0.6rem;
  border-radius: 4px;
  font-size: 0.8rem;
  font-weight: 600;
}

.session-badge {
  padding: 0.25rem 0.75rem;
  border-radius: 12px;
  font-size: 0.8rem;
  font-weight: 600;
  background: #e0e7ff;
  color: #3730a3;
}

.schedule-badge {
  padding: 0.25rem 0.75rem;
  border-radius: 12px;
  font-size: 0.8rem;
  font-weight: 600;
}

.schedule-badge.commencement {
  background: #d1fae5;
  color: #065f46;
}

.schedule-badge.adjournment {
  background: #fee2e2;
  color: #dc2626;
}

.schedule-badge.resumption {
  background: #dbeafe;
  color: #2563eb;
}

.schedule-badge.suspension {
  background: #fef3c7;
  color: #d97706;
}

.actions {
  display: flex;
  gap: 0.5rem;
}

.modal-form {
  padding: 0;
}

.form-grid {
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
  padding: 1.5rem;
}

.form-group {
  display: flex;
  flex-direction: column;
}

.form-group .p-select,
.form-group .p-datepicker {
  width: 100% !important;
}

.form-row-2 {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;
}

.modal-actions {
  display: flex;
  justify-content: flex-end;
  gap: 0.75rem;
  padding: 1.5rem;
  border-top: 1px solid #e5e7eb;
  background: #f8fafc;
}

@media (max-width: 768px) {
  .container {
    padding: 0 1rem;
  }

  .table-header {
    flex-direction: column;
    align-items: stretch;
  }

  .header-filters {
    flex-direction: column;
  }

  .form-row-2 {
    grid-template-columns: 1fr;
  }
}
</style>
